import React, { useState } from "react";
import { useAtomValue } from "jotai";
import {
  Box,
  Flex,
  Grid,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  VStack,
  Badge,
  Avatar,
  HStack,
  Divider,
} from "@chakra-ui/react";
import { Config } from "./config/Config";
import { Instruments } from "./instruments/Instruments";
import { AppStateAtom } from "../../store";
import Users from "./users/Users";
import Files from "./files/Files";
import WebsiteSettings from "./settings/WebsiteSettings";
import TradeConfirmation from "./tradeconfirmation/TradeConfirmation";
import KnowledgeBaseAdmin from "./knowledgebase/KnowledgeBaseAdmin";
import Images from "./images/Images";
import BlogAdmin from "./blogs/BlogAdmin";
import { EmailIcon, SettingsIcon, SearchIcon } from "@chakra-ui/icons";
import { FaFileUpload, FaGuitar, FaQuestion } from "react-icons/fa";
import { RiListIndefinite, RiUserSearchLine } from "react-icons/ri";
import { BiNews, BiPaint, BiPhotoAlbum } from "react-icons/bi";
import { BsCashStack } from "react-icons/bs";
import CashflowsAdmin from "./cashflows/CashflowsAdmin";
import { IoAnalytics } from "react-icons/io5";
import Analysis from "./analysis/Analysis";
import FeedbackAdmin from "./feedback/FeedbackAdmin";
import { MdFeedback } from "react-icons/md";
import SnapTradeAdmin from "./snaptrade/SnapTrade";
import DisputesAdmin from "./disputes/DisputesAdmin";

export const Admin = () => {
  const appState = useAtomValue(AppStateAtom);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState(-1); // Start with no tab selected

  // UI colors
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const headerBg = useColorModeValue("gray.50", "gray.900");
  const tabHoverBg = useColorModeValue("gray.100", "gray.700");
  const activeBg = useColorModeValue("blue.50", "blue.900");
  const activeColor = useColorModeValue("blue.600", "blue.300");

  // Admin tabs configuration with icons
  const adminTabs = [
    { name: "Users", icon: RiUserSearchLine },
    { name: "Instruments", icon: FaGuitar },
    { name: "Config", icon: RiListIndefinite },
    { name: "Uploads", icon: FaFileUpload },
    { name: "Settings", icon: SettingsIcon },
    { name: "Trade Confirmations", icon: EmailIcon },
    { name: "FAQ", icon: FaQuestion },
    { name: "Blog", icon: BiNews },
    { name: "Images", icon: BiPhotoAlbum },
    { name: "White-label", icon: BiPaint },
    { name: "Cashflows", icon: BsCashStack },
    { name: "Analysis", icon: IoAnalytics },
    { name: "Feedback", icon: MdFeedback },
    { name: "SnapTrade", icon: RiListIndefinite },
    { name: "Disputes", icon: MdFeedback },
  ];

  if (!appState.authed || appState.user?.role !== "admin") {
    return null;
  }

  return (
    <Flex h="full" w="full" direction="column">
      <Grid
        templateColumns={{ base: "1fr", md: "250px 1fr" }}
        h="100%" // Account for header height
      >
        {/* Sidebar Tabs */}
        <Box
          borderColor={borderColor}
          bg={useColorModeValue("gray.50", "gray.900")}
          overflowY="auto"
        >
          {/* Search Input */}
          <Box p={4} borderBottom="1px solid" borderColor={borderColor}>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search tabs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                bg={useColorModeValue("white", "gray.800")}
                border="1px solid"
                borderColor={borderColor}
                _focus={{
                  borderColor: activeColor,
                  boxShadow: "none",
                }}
              />
            </InputGroup>
          </Box>
          
          <VStack w="full" spacing={0}>
            {adminTabs
              .filter((tab) =>
                tab.name.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map((tab, filteredIndex) => {
                const originalIndex = adminTabs.findIndex(originalTab => originalTab.name === tab.name);
                const isActive = activeTab === originalIndex;
                
                return (
                  <Box
                    key={originalIndex}
                    w="full"
                    py={3}
                    px={4}
                    cursor="pointer"
                    bg={isActive ? activeBg : "transparent"}
                    color={isActive ? activeColor : "inherit"}
                    fontWeight={isActive ? "semibold" : "normal"}
                    _hover={{ bg: tabHoverBg }}
                    transition="all 0.2s"
                    borderLeft="3px solid"
                    borderLeftColor={isActive ? activeColor : "transparent"}
                    onClick={() => setActiveTab(originalIndex)}
                  >
                    <HStack spacing={3}>
                      <Icon as={tab.icon} fontSize="18px" />
                      <Text>{tab.name}</Text>
                    </HStack>
                  </Box>
                );
              })}
          </VStack>
        </Box>

        {/* Content Area */}
        <Box bg={bgColor} overflowY="auto" h="full" shadow="sm">
          {activeTab === -1 ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              h="full"
              p={8}
            >
              <VStack spacing={4}>
                <Heading size="lg" color="gray.500">
                  Admin Panel
                </Heading>
                <Text color="gray.500" textAlign="center">
                  Select a tab from the sidebar to get started
                </Text>
              </VStack>
            </Box>
          ) : (
            <Box h="full">
              {activeTab === 0 && <Users />}
              {activeTab === 1 && <Instruments />}
              {activeTab === 2 && <Config />}
              {activeTab === 3 && <Files />}
              {activeTab === 4 && <WebsiteSettings />}
              {activeTab === 5 && <TradeConfirmation />}
              {activeTab === 6 && <KnowledgeBaseAdmin />}
              {activeTab === 7 && <BlogAdmin />}
              {activeTab === 8 && <Images />}
              {activeTab === 9 && (
                <Box
                  bg={useColorModeValue("white", "gray.800")}
                  p={6}
                  rounded="md"
                  shadow="sm"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <VStack spacing={6} align="start">
                    <Heading size="lg">White Labeling Settings</Heading>
                    <Divider />

                    <Box>
                      <Heading size="sm" mb={2}>
                        Theme Configuration
                      </Heading>
                      <Text fontSize="sm" color="gray.600" mb={4}>
                        This section allows you to modify the theme in the URL.
                        The theme you select here will be applied and persist
                        until the page is refreshed.
                      </Text>
                    </Box>

                    <Box
                      p={4}
                      bg={useColorModeValue("gray.50", "gray.700")}
                      rounded="md"
                      w="full"
                    >
                      <Heading size="xs" mb={2}>
                        Tasty Theme
                      </Heading>
                      <Text mb={2} fontSize="sm">
                        Click the link below to apply the "Tasty" theme. This
                        will update the theme for the dashboard to
                        'ag-theme-tasty':
                      </Text>
                      <Box
                        as="a"
                        href={`${window.location.origin}/app/dashboard?theme=ag-theme-tasty`}
                        target="_blank"
                        color="blue.500"
                        fontSize="sm"
                        _hover={{ textDecoration: "underline" }}
                      >
                        Tasty Theme Link:{" "}
                        {`${window.location.origin}/app/dashboard?theme=ag-theme-tasty`}
                      </Box>
                    </Box>

                    <Box
                      p={4}
                      bg={useColorModeValue("gray.50", "gray.700")}
                      rounded="md"
                      w="full"
                    >
                      <Heading size="xs" mb={2}>
                        Current Theme
                      </Heading>
                      <Text mb={2} fontSize="sm">
                        This link applies the theme that is currently in use in
                        the app (stored in the state). It shows your current
                        theme based on what is set in the app state:
                      </Text>
                      <Box
                        as="a"
                        href={`${window.location.origin}/app/dashboard?theme=${appState.gridTheme}`}
                        target="_blank"
                        color="blue.500"
                        fontSize="sm"
                        _hover={{ textDecoration: "underline" }}
                      >
                        Current Theme Link:{" "}
                        {`${window.location.origin}/app/dashboard?theme=${appState.gridTheme}`}
                      </Box>
                    </Box>

                    <Box
                      p={4}
                      bg={useColorModeValue("gray.50", "gray.700")}
                      rounded="md"
                      w="full"
                    >
                      <Heading size="xs" mb={2}>
                        Example URL Format
                      </Heading>
                      <Text mb={2} fontSize="sm">
                        Below is an example URL that shows how you can customize
                        the dashboard with specific parameters like
                        "whitelabel", "portfolioId", and "dashboardId":
                      </Text>
                      <Box
                        p={2}
                        bg={useColorModeValue("gray.100", "gray.600")}
                        rounded="md"
                        fontSize="sm"
                        fontFamily="mono"
                      >
                        http://localhost:3000/app/dashboard?whitelabel=true&portfolioId=94&dashboardId=285
                      </Box>
                    </Box>
                  </VStack>
                </Box>
              )}
              {activeTab === 10 && <CashflowsAdmin />}
              {activeTab === 11 && <Analysis />}
              {activeTab === 12 && <FeedbackAdmin />}
              {activeTab === 13 && <SnapTradeAdmin />}
              {activeTab === 14 && <DisputesAdmin />}
            </Box>
          )}
        </Box>
      </Grid>
    </Flex>
  );
};
